import { DefaultTemplate, COR, CORES_SIM_NAO } from './types';

// ============================================================
// SUPPLY CHAIN — distribuidores, atacado e centro de distribuição
//
// Complementa o "Movimentação de Estoque" que já existia, cobrindo o ciclo
// completo: planejar a compra, receber na doca, separar e expedir com OTIF,
// medir acuracidade por inventário e tratar o que volta (reversa).
// A parte de chão de loja está em ./varejo.ts.
// ============================================================

const pedidoExpedicao: DefaultTemplate = {
  id: 'tpl_pedido_expedicao',
  name: 'Pedido de Venda à Expedição (OTIF)',
  descricao: 'Do pedido recebido à entrega confirmada: análise de crédito, separação com conferência, faturamento, carregamento e medição de OTIF (no prazo e completo). O ciclo que define o nível de serviço do distribuidor.',
  categoria: 'logistica',
  icone: '🚚',
  etiquetas: [
    { nome: 'Cliente Chave', cor: COR.roxo },
    { nome: 'Entrega Programada', cor: COR.azul },
    { nome: 'Bloqueado', cor: COR.vermelho },
  ],
  etapas: [
    {
      id: 'pe_e1', nome: 'Pedido Recebido',
      tempoMaximoHoras: 12,
      camposVisiveisNoCard: ['pe_c1', 'pe_c2', 'pe_c6'],
      permiteMoverPara: ['pe_e2'],
      campos: [
        { id: 'pe_c1', nome: 'Pedido / Cliente', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'pe_c2', nome: 'Nº do Pedido', tipo: 'texto', obrigatorio: true },
        { id: 'pe_c3', nome: 'Vendedor / Representante', tipo: 'texto', obrigatorio: false },
        { id: 'pe_c4', nome: 'Canal', tipo: 'lista', obrigatorio: false, opcoes: ['Força de vendas', 'Televendas', 'E-commerce B2B', 'EDI', 'WhatsApp', 'Balcão'] },
        { id: 'pe_c5', nome: 'Qtd. de Itens', tipo: 'numero', obrigatorio: false },
        { id: 'pe_c6', nome: 'Valor do Pedido', tipo: 'valor', obrigatorio: true },
        { id: 'pe_c7', nome: 'Data Prometida de Entrega', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'pe_c8', nome: 'Condição de Pagamento', tipo: 'texto', obrigatorio: false },
        { id: 'pe_c9', nome: 'Observações do Pedido', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'pe_e2', nome: 'Crédito e Disponibilidade',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['pe_c1', 'pe_c10', 'pe_c12'],
      permiteMoverPara: ['pe_e3', 'pe_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'pe_c10', nome: 'Crédito aprovado?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'pe_c11', nome: 'Motivo do Bloqueio', tipo: 'lista', obrigatorio: false, opcoes: ['Limite de crédito excedido', 'Títulos em atraso', 'Cliente sem cadastro completo', 'Restrição em bureau', 'Aguardando pagamento antecipado'], coresOpcoes: { 'Títulos em atraso': COR.vermelho } },
        { id: 'pe_c12', nome: 'Estoque disponível para todos os itens?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'pe_c13', nome: 'Itens Indisponíveis', tipo: 'texto_longo', obrigatorio: false },
        { id: 'pe_c14', nome: 'Cliente aceita entrega parcial?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'pe_e3', nome: 'Separação',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['pe_c1', 'pe_c16', 'pe_c7'],
      permiteMoverPara: ['pe_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'pe_c15', nome: 'Separador Responsável', tipo: 'texto', obrigatorio: false },
        { id: 'pe_c16', nome: 'Progresso da Separação', tipo: 'progresso', obrigatorio: false },
        { id: 'pe_c17', nome: 'Nº de Volumes', tipo: 'numero', obrigatorio: false },
        { id: 'pe_c18', nome: 'Conferência realizada?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'pe_c19', nome: 'Divergência na Separação', tipo: 'texto_longo', obrigatorio: false },
        { id: 'pe_c20', nome: 'Separação concluída?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'pe_e4', nome: 'Faturamento',
      tempoMaximoHoras: 12,
      camposVisiveisNoCard: ['pe_c1', 'pe_c21', 'pe_c23'],
      permiteMoverPara: ['pe_e5'],
      permiteCriarCards: false,
      campos: [
        { id: 'pe_c21', nome: 'Nº da Nota Fiscal', tipo: 'texto', obrigatorio: false },
        { id: 'pe_c22', nome: 'Data de Emissão', tipo: 'data', obrigatorio: false },
        { id: 'pe_c23', nome: 'Valor Faturado', tipo: 'valor', obrigatorio: false },
        { id: 'pe_c24', nome: 'NF autorizada pela SEFAZ?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'pe_c25', nome: 'DANFE / XML', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'pe_e5', nome: 'Expedição / Carregamento',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['pe_c1', 'pe_c26', 'pe_c29'],
      permiteMoverPara: ['pe_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'pe_c26', nome: 'Transportadora / Frota', tipo: 'texto', obrigatorio: false },
        { id: 'pe_c27', nome: 'Motorista / Veículo', tipo: 'texto', obrigatorio: false },
        { id: 'pe_c28', nome: 'Nº da Carga / Romaneio', tipo: 'texto', obrigatorio: false },
        { id: 'pe_c29', nome: 'Data do Carregamento', tipo: 'data', obrigatorio: false },
        { id: 'pe_c30', nome: 'Tipo de Frete', tipo: 'lista', obrigatorio: false, opcoes: ['CIF (nosso)', 'FOB (cliente)', 'Transportadora terceirizada', 'Retirada no CD'] },
        { id: 'pe_c31', nome: 'Carga expedida?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'pe_e6', nome: 'Entregue',
      camposVisiveisNoCard: ['pe_c1', 'pe_c32', 'pe_c34'],
      permiteCriarCards: false,
      campos: [
        { id: 'pe_c32', nome: 'Data da Entrega', tipo: 'data', obrigatorio: false },
        { id: 'pe_c33', nome: 'Entregue no prazo?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'pe_c34', nome: 'Entregue completo?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'pe_c35', nome: 'Ocorrência na Entrega', tipo: 'lista', obrigatorio: false, opcoes: ['Sem ocorrência', 'Recusa parcial', 'Recusa total', 'Avaria no transporte', 'Cliente ausente', 'Endereço não localizado', 'Atraso na janela de recebimento'], coresOpcoes: { 'Sem ocorrência': COR.verde, 'Recusa total': COR.vermelho, 'Avaria no transporte': COR.vermelho } },
        { id: 'pe_c36', nome: 'Comprovante de Entrega', tipo: 'anexo', obrigatorio: false },
        { id: 'pe_c37', nome: 'Observação', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'pe_e7', nome: 'Pedido Retido',
      camposVisiveisNoCard: ['pe_c1', 'pe_c38', 'pe_c39'],
      permiteMoverPara: ['pe_e2', 'pe_e3'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'pe_c38', nome: 'Motivo da Retenção', tipo: 'lista', obrigatorio: true, opcoes: ['Bloqueio de crédito', 'Falta de estoque', 'Cliente pediu para segurar', 'Divergência de preço', 'Aguardando pagamento', 'Pedido cancelado'], coresOpcoes: { 'Bloqueio de crédito': COR.vermelho, 'Pedido cancelado': COR.cinza } },
        { id: 'pe_c39', nome: 'Prazo para Resolver', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'pe_c40', nome: 'Tratativa', tipo: 'texto_longo', obrigatorio: false },
        { id: 'pe_c41', nome: 'Comercial foi avisado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Crédito aprovado libera a separação',
      etapaOrigemId: 'pe_e2',
      condicao: { campoId: 'pe_c10', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'pe_e3',
      etapaDestinoSenaoId: 'pe_e7',
    },
    {
      tipo: 'mover',
      nome: 'Separação concluída vai para faturamento',
      etapaOrigemId: 'pe_e3',
      condicao: { campoId: 'pe_c20', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'pe_e4',
    },
    {
      tipo: 'mover',
      nome: 'NF autorizada libera o carregamento',
      etapaOrigemId: 'pe_e4',
      condicao: { campoId: 'pe_c24', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'pe_e5',
    },
    {
      tipo: 'mover',
      nome: 'Carga expedida entra em rota',
      etapaOrigemId: 'pe_e5',
      condicao: { campoId: 'pe_c31', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'pe_e6',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Itens indisponíveis quando falta estoque',
      etapaOrigemId: 'pe_e2',
      condicao: { campoId: 'pe_c12', operador: 'igual', valor: 'Não' },
      camposAfetadosIds: ['pe_c13', 'pe_c14'],
    },
  ],
  cards: [
    { id: 'pe_card1', titulo: 'Rede Supermercados Vale - Pedido 88412', etapaId: 'pe_e3', etiquetas: ['Cliente Chave'], valores: { pe_c1: 'Rede Supermercados Vale - Pedido 88412', pe_c2: '88412', pe_c4: 'EDI', pe_c5: 84, pe_c6: 142000, pe_c10: 'Sim', pe_c12: 'Sim', pe_c16: 60 } },
    { id: 'pe_card2', titulo: 'Mercado Central - Pedido 88455', etapaId: 'pe_e7', etiquetas: ['Bloqueado'], valores: { pe_c1: 'Mercado Central - Pedido 88455', pe_c2: '88455', pe_c4: 'Força de vendas', pe_c6: 28400, pe_c10: 'Não', pe_c11: 'Títulos em atraso', pe_c38: 'Bloqueio de crédito', pe_c41: 'Sim' } },
    { id: 'pe_card3', titulo: 'Atacado Sul - Pedido 88390', etapaId: 'pe_e5', etiquetas: ['Entrega Programada'], valores: { pe_c1: 'Atacado Sul - Pedido 88390', pe_c2: '88390', pe_c6: 96000, pe_c21: '447112', pe_c26: 'Transportes Andrade', pe_c30: 'CIF (nosso)' } },
    { id: 'pe_card4', titulo: 'Padaria Estrela - Pedido 88478', etapaId: 'pe_e6', valores: { pe_c1: 'Padaria Estrela - Pedido 88478', pe_c2: '88478', pe_c6: 6400, pe_c33: 'Sim', pe_c34: 'Sim', pe_c35: 'Sem ocorrência' } },
    { id: 'pe_card5', titulo: 'Distribuidora Norte - Pedido 88501', etapaId: 'pe_e1', valores: { pe_c1: 'Distribuidora Norte - Pedido 88501', pe_c2: '88501', pe_c4: 'Televendas', pe_c5: 32, pe_c6: 54000 } },
    { id: 'pe_card6', titulo: 'Rede Bom Preço - Pedido 88433', etapaId: 'pe_e6', etiquetas: ['Cliente Chave'], valores: { pe_c1: 'Rede Bom Preço - Pedido 88433', pe_c2: '88433', pe_c6: 210000, pe_c33: 'Não', pe_c34: 'Sim', pe_c35: 'Atraso na janela de recebimento' } },
    { id: 'pe_card7', titulo: 'Mercearia do Bairro - Pedido 88462', etapaId: 'pe_e4', valores: { pe_c1: 'Mercearia do Bairro - Pedido 88462', pe_c2: '88462', pe_c6: 12800, pe_c21: '447098', pe_c23: 12800 } },
  ],
  tour: [
    '🚚 O ciclo do pedido inteiro em colunas, com OTIF medido no fim.',
    '1️⃣ "Pedido Recebido": a data prometida vira vencimento do card — é ela que define o "on time".',
    '2️⃣ "Crédito e Disponibilidade": bloqueou? o card vai para retenção e o comercial é avisado.',
    '3️⃣ "Separação": progresso e conferência antes de faturar.',
    '4️⃣ "Faturamento": NF autorizada pela SEFAZ libera o carregamento.',
    '5️⃣ "Expedição": transportadora, romaneio e tipo de frete.',
    '📊 "Entregue": no prazo? completo? Esses dois campos são o OTIF do mês.',
    '⛔ "Pedido Retido": pedido parado não some — fica visível com prazo para resolver.',
  ],
  etapasDicas: [
    { quemFaz: 'Televendas / Vendedor', objetivo: 'Registrar a data prometida com realismo — é o que será cobrado.' },
    { quemFaz: 'Crédito e Cobrança', objetivo: 'Liberar ou reter rápido; pedido parado em análise é venda perdida.', automacoes: ['Já configurado: bloqueado vai para retenção'] },
    { quemFaz: 'Equipe de separação', objetivo: 'Conferir antes de fechar o volume — erro aqui vira devolução.' },
    { quemFaz: 'Faturamento', objetivo: 'Emitir e validar a autorização da SEFAZ.' },
    { quemFaz: 'Expedição', objetivo: 'Amarrar carga, motorista e romaneio.', conexoes: ['Ligue a "Devoluções e Logística Reversa" quando houver recusa'] },
    { quemFaz: 'Logística / Atendimento', objetivo: 'Fechar o OTIF com honestidade — indicador maquiado não melhora nada.' },
    { quemFaz: 'Comercial / Crédito', objetivo: 'Destravar o pedido dentro do prazo.' },
  ],
};

const recebimentoDocas: DefaultTemplate = {
  id: 'tpl_recebimento_docas',
  name: 'Recebimento e Conferência (Docas)',
  descricao: 'Agendamento de janela, fila de veículos, conferência de quantidade, validade e temperatura, tratamento de divergência e endereçamento no estoque. Reduz caminhão parado e nota fiscal aceita errada.',
  categoria: 'logistica',
  icone: '📥',
  etiquetas: [
    { nome: 'Refrigerado', cor: COR.azul },
    { nome: 'Carga Crítica', cor: COR.vermelho },
    { nome: 'Fornecedor Reincidente', cor: COR.amarelo },
  ],
  etapas: [
    {
      id: 'rc_e1', nome: 'Agendamento',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['rc_c1', 'rc_c2', 'rc_c5'],
      permiteMoverPara: ['rc_e2'],
      campos: [
        { id: 'rc_c1', nome: 'Carga / Fornecedor', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'rc_c2', nome: 'Nº da Nota Fiscal', tipo: 'texto', obrigatorio: false },
        { id: 'rc_c3', nome: 'Nº do Pedido de Compra', tipo: 'texto', obrigatorio: false },
        { id: 'rc_c4', nome: 'Tipo de Carga', tipo: 'lista', obrigatorio: true, opcoes: ['Seca / mercearia', 'Refrigerada', 'Congelada', 'Hortifruti', 'Carnes', 'Bazar / não alimentar', 'Paletizada', 'Batida (não paletizada)'], coresOpcoes: { 'Refrigerada': COR.azul, 'Congelada': COR.azul, 'Batida (não paletizada)': COR.amarelo } },
        { id: 'rc_c5', nome: 'Janela Agendada', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'rc_c6', nome: 'Volumes Previstos', tipo: 'numero', obrigatorio: false },
        { id: 'rc_c7', nome: 'Valor da Nota', tipo: 'valor', obrigatorio: false },
        { id: 'rc_c8', nome: 'Transportadora', tipo: 'texto', obrigatorio: false },
        { id: 'rc_c9', nome: 'XML / DANFE', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'rc_e2', nome: 'Chegada e Fila',
      tempoMaximoHoras: 8,
      camposVisiveisNoCard: ['rc_c1', 'rc_c10', 'rc_c12'],
      permiteMoverPara: ['rc_e3', 'rc_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'rc_c10', nome: 'Hora de Chegada', tipo: 'texto', obrigatorio: false },
        { id: 'rc_c11', nome: 'Placa do Veículo', tipo: 'texto', obrigatorio: false },
        { id: 'rc_c12', nome: 'Doca Designada', tipo: 'texto', obrigatorio: false },
        { id: 'rc_c13', nome: 'Chegou dentro da janela?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'rc_c14', nome: 'Documentação em ordem?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'rc_c15', nome: 'Tempo de Espera (min)', tipo: 'numero', obrigatorio: false },
      ],
    },
    {
      id: 'rc_e3', nome: 'Conferência',
      tempoMaximoHoras: 12,
      camposVisiveisNoCard: ['rc_c1', 'rc_c17', 'rc_c19'],
      permiteMoverPara: ['rc_e4', 'rc_e5'],
      permiteCriarCards: false,
      campos: [
        { id: 'rc_c16', nome: 'Itens Verificados', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['Quantidade x nota fiscal', 'Preço x pedido de compra', 'Validade mínima aceita', 'Integridade das embalagens', 'Temperatura da carga', 'Lote e rastreabilidade', 'Paletização e empilhamento', 'Laudo / certificado do produto'] },
        { id: 'rc_c17', nome: 'Carga conforme?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'rc_c18', nome: 'Conferente', tipo: 'texto', obrigatorio: false },
        { id: 'rc_c19', nome: 'Temperatura Registrada', tipo: 'texto', obrigatorio: false },
        { id: 'rc_c20', nome: 'Volumes Recebidos', tipo: 'numero', obrigatorio: false },
        { id: 'rc_c21', nome: 'Fotos da Conferência', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'rc_e4', nome: 'Divergência',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['rc_c1', 'rc_c22', 'rc_c24'],
      permiteMoverPara: ['rc_e5', 'rc_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'rc_c22', nome: 'Tipo de Divergência', tipo: 'lista', obrigatorio: true, opcoes: ['Falta de produto', 'Sobra de produto', 'Produto avariado', 'Validade curta', 'Preço divergente do pedido', 'Item não pedido', 'Temperatura fora do padrão', 'Nota fiscal com erro'], coresOpcoes: { 'Produto avariado': COR.vermelho, 'Temperatura fora do padrão': COR.vermelho, 'Preço divergente do pedido': COR.amarelo } },
        { id: 'rc_c23', nome: 'Descrição da Divergência', tipo: 'texto_longo', obrigatorio: true },
        { id: 'rc_c24', nome: 'Valor Divergente', tipo: 'valor', obrigatorio: false },
        { id: 'rc_c25', nome: 'Decisão', tipo: 'lista', obrigatorio: false, opcoes: ['Receber parcial', 'Recusar totalmente', 'Receber com ressalva', 'Aguardar autorização do comprador'], coresOpcoes: { 'Recusar totalmente': COR.vermelho, 'Receber parcial': COR.amarelo } },
        { id: 'rc_c26', nome: 'Comprador Acionado', tipo: 'texto', obrigatorio: false },
        { id: 'rc_c27', nome: 'Divergência resolvida?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'rc_e5', nome: 'Endereçado no Estoque',
      tempoMaximoHoras: 12,
      camposVisiveisNoCard: ['rc_c1', 'rc_c28', 'rc_c30'],
      permiteCriarCards: false,
      campos: [
        { id: 'rc_c28', nome: 'Endereço / Rua no Estoque', tipo: 'texto', obrigatorio: false },
        { id: 'rc_c29', nome: 'Data e Hora do Endereçamento', tipo: 'data', obrigatorio: false },
        { id: 'rc_c30', nome: 'Entrada lançada no sistema?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'rc_c31', nome: 'Tempo Total de Descarga (min)', tipo: 'numero', obrigatorio: false },
        { id: 'rc_c32', nome: 'Responsável', tipo: 'texto', obrigatorio: false },
      ],
    },
    {
      id: 'rc_e6', nome: 'Recusado',
      camposVisiveisNoCard: ['rc_c1', 'rc_c33'],
      permiteMoverPara: ['rc_e1'],
      permiteCriarCards: false,
      campos: [
        { id: 'rc_c33', nome: 'Motivo da Recusa', tipo: 'texto_longo', obrigatorio: true },
        { id: 'rc_c34', nome: 'Nota Fiscal devolvida?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'rc_c35', nome: 'Fornecedor notificado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'rc_c36', nome: 'Reagendamento Previsto', tipo: 'data', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Documentação em ordem libera a conferência',
      etapaOrigemId: 'rc_e2',
      condicao: { campoId: 'rc_c14', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'rc_e3',
      etapaDestinoSenaoId: 'rc_e6',
    },
    {
      tipo: 'mover',
      nome: 'Carga conforme vai direto para o endereçamento',
      etapaOrigemId: 'rc_e3',
      condicao: { campoId: 'rc_c17', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'rc_e5',
      etapaDestinoSenaoId: 'rc_e4',
    },
    {
      tipo: 'mover',
      nome: 'Divergência resolvida segue para o estoque',
      etapaOrigemId: 'rc_e4',
      condicao: { campoId: 'rc_c27', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'rc_e5',
      etapaDestinoSenaoId: 'rc_e6',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Temperatura só para carga refrigerada',
      etapaOrigemId: 'rc_e3',
      condicao: { campoId: 'rc_c17', operador: 'igual', valor: 'Não' },
      camposAfetadosIds: ['rc_c21'],
    },
  ],
  cards: [
    { id: 'rc_card1', titulo: 'Laticínios Vale - NF 112844', etapaId: 'rc_e4', etiquetas: ['Refrigerado', 'Carga Crítica'], valores: { rc_c1: 'Laticínios Vale - NF 112844', rc_c2: '112844', rc_c4: 'Refrigerada', rc_c7: 84000, rc_c17: 'Não', rc_c19: '11°C (limite 8°C)', rc_c22: 'Temperatura fora do padrão', rc_c23: 'Carga chegou a 11°C. Baú com falha no termostato.', rc_c25: 'Recusar totalmente' } },
    { id: 'rc_card2', titulo: 'Alimentos Vale - NF 447120', etapaId: 'rc_e3', valores: { rc_c1: 'Alimentos Vale - NF 447120', rc_c2: '447120', rc_c4: 'Seca / mercearia', rc_c6: 42, rc_c7: 128000, rc_c13: 'Sim' } },
    { id: 'rc_card3', titulo: 'Química Brasil - NF 88214', etapaId: 'rc_e5', valores: { rc_c1: 'Química Brasil - NF 88214', rc_c2: '88214', rc_c4: 'Paletizada', rc_c7: 46000, rc_c28: 'Rua 12 - Nível B', rc_c30: 'Sim', rc_c31: 48 } },
    { id: 'rc_card4', titulo: 'Hortifruti Campo - carga do dia', etapaId: 'rc_e2', etiquetas: ['Refrigerado'], valores: { rc_c1: 'Hortifruti Campo - carga do dia', rc_c4: 'Hortifruti', rc_c6: 28, rc_c10: '05:40', rc_c12: 'Doca 3', rc_c13: 'Sim' } },
    { id: 'rc_card5', titulo: 'BebidasMax - NF 33900', etapaId: 'rc_e1', valores: { rc_c1: 'BebidasMax - NF 33900', rc_c2: '33900', rc_c4: 'Paletizada', rc_c6: 60, rc_c7: 96000, rc_c8: 'Transportes Andrade' } },
    { id: 'rc_card6', titulo: 'Distribuidora Norte - NF 55120', etapaId: 'rc_e6', etiquetas: ['Fornecedor Reincidente'], valores: { rc_c1: 'Distribuidora Norte - NF 55120', rc_c2: '55120', rc_c4: 'Seca / mercearia', rc_c33: 'Nota fiscal emitida com CNPJ da filial errada pela terceira vez no mês.', rc_c34: 'Sim', rc_c35: 'Sim' } },
  ],
  tour: [
    '📥 Doca organizada: agendamento, fila medida e conferência com critério.',
    '1️⃣ "Agendamento": janela vira vencimento — fornecedor fora da janela aparece no relatório.',
    '2️⃣ "Chegada e Fila": tempo de espera registrado; é o que negocia com transportadora depois.',
    '3️⃣ "Conferência": checklist com validade mínima, temperatura e preço x pedido.',
    '4️⃣ "Divergência": decisão explícita — receber parcial, com ressalva ou recusar.',
    '5️⃣ "Endereçado": entrada lançada e tempo total de descarga medido.',
    '⛔ "Recusado": fornecedor notificado e reagendamento previsto.',
  ],
  etapasDicas: [
    { quemFaz: 'Recebimento / Agendamento', objetivo: 'Distribuir janelas para não empilhar caminhão na porta.' },
    { quemFaz: 'Portaria / Recebimento', objetivo: 'Medir espera — caminhão parado é custo que volta no frete.' },
    { quemFaz: 'Conferente', objetivo: 'Conferir preço contra o pedido, não só quantidade.', automacoes: ['Já configurado: carga conforme vai direto ao endereçamento'] },
    { quemFaz: 'Conferente + Comprador', objetivo: 'Decidir a divergência na doca, com o caminhão ainda ali.' },
    { quemFaz: 'Estoquista', objetivo: 'Endereçar e lançar a entrada no mesmo dia — atraso aqui gera ruptura na loja.', conexoes: ['Ligue a "Ruptura e Reposição de Gôndola"'] },
    { quemFaz: 'Recebimento / Compras', objetivo: 'Notificar o fornecedor; recusa sem registro não muda comportamento.' },
  ],
};

const inventarioAcuracidade: DefaultTemplate = {
  id: 'tpl_inventario_acuracidade',
  name: 'Inventário e Acuracidade',
  descricao: 'Inventário rotativo, cíclico ou geral: programação por área, contagem às cegas, recontagem das divergências, análise de causa e ajuste aprovado. Mede a acuracidade, que é a raiz de quase toda ruptura.',
  categoria: 'logistica',
  icone: '🔢',
  etiquetas: [
    { nome: 'Divergência Alta', cor: COR.vermelho },
    { nome: 'Inventário Geral', cor: COR.roxo },
    { nome: 'Curva A', cor: COR.amarelo },
  ],
  etapas: [
    {
      id: 'iv_e1', nome: 'Programação',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['iv_c1', 'iv_c2', 'iv_c5'],
      permiteMoverPara: ['iv_e2'],
      campos: [
        { id: 'iv_c1', nome: 'Inventário', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'iv_c2', nome: 'Tipo', tipo: 'lista', obrigatorio: true, opcoes: ['Rotativo (por curva)', 'Cíclico (por endereço)', 'Geral / balanço', 'Pontual (por suspeita)', 'Fiscal / contábil'], coresOpcoes: { 'Geral / balanço': COR.roxo, 'Pontual (por suspeita)': COR.amarelo } },
        { id: 'iv_c3', nome: 'Local / Área', tipo: 'texto', obrigatorio: true },
        { id: 'iv_c4', nome: 'Escopo (seções / endereços)', tipo: 'texto_longo', obrigatorio: false },
        { id: 'iv_c5', nome: 'Data Programada', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'iv_c6', nome: 'Nº de SKUs a Contar', tipo: 'numero', obrigatorio: false },
        { id: 'iv_c7', nome: 'Equipe Designada', tipo: 'texto', obrigatorio: false },
        { id: 'iv_c8', nome: 'Operação será paralisada?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'iv_e2', nome: '1ª Contagem',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['iv_c1', 'iv_c9', 'iv_c11'],
      permiteMoverPara: ['iv_e3', 'iv_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'iv_c9', nome: 'Progresso da Contagem', tipo: 'progresso', obrigatorio: false },
        { id: 'iv_c10', nome: 'Contagem às cegas?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'iv_c11', nome: 'SKUs com Divergência', tipo: 'numero', obrigatorio: false },
        { id: 'iv_c12', nome: 'Planilha de Contagem', tipo: 'anexo', obrigatorio: false },
        { id: 'iv_c13', nome: 'Precisa de recontagem?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'iv_e3', nome: 'Recontagem',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['iv_c1', 'iv_c14', 'iv_c16'],
      permiteMoverPara: ['iv_e4'],
      permiteCriarCards: false,
      campos: [
        { id: 'iv_c14', nome: 'SKUs Recontados', tipo: 'numero', obrigatorio: false },
        { id: 'iv_c15', nome: 'Equipe da Recontagem', tipo: 'texto', obrigatorio: false },
        { id: 'iv_c16', nome: 'Divergências Confirmadas', tipo: 'numero', obrigatorio: false },
        { id: 'iv_c17', nome: 'Recontagem concluída?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'iv_c18', nome: 'Observações', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'iv_e4', nome: 'Análise e Justificativa',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['iv_c1', 'iv_c19', 'iv_c21'],
      permiteMoverPara: ['iv_e5'],
      permiteCriarCards: false,
      campos: [
        { id: 'iv_c19', nome: 'Acuracidade (%)', tipo: 'numero', obrigatorio: false },
        { id: 'iv_c20', nome: 'Causas Identificadas', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['Erro de recebimento', 'Erro de separação', 'Baixa de quebra não lançada', 'Transferência não registrada', 'Furto / desvio', 'Erro de cadastro de embalagem', 'Venda sem baixa (PDV)', 'Endereçamento errado'] },
        { id: 'iv_c21', nome: 'Valor da Diferença', tipo: 'valor', obrigatorio: false },
        { id: 'iv_c22', nome: 'Diferença a Maior', tipo: 'valor', obrigatorio: false },
        { id: 'iv_c23', nome: 'Diferença a Menor', tipo: 'valor', obrigatorio: false },
        { id: 'iv_c24', nome: 'Justificativa', tipo: 'texto_longo', obrigatorio: false },
        { id: 'iv_c25', nome: 'Ajuste aprovado?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'iv_e5', nome: 'Ajuste Lançado',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['iv_c1', 'iv_c26', 'iv_c27'],
      permiteMoverPara: ['iv_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'iv_c26', nome: 'Documento do Ajuste', tipo: 'texto', obrigatorio: false },
        { id: 'iv_c27', nome: 'Valor Ajustado', tipo: 'valor', obrigatorio: false },
        { id: 'iv_c28', nome: 'Data do Lançamento', tipo: 'data', obrigatorio: false },
        { id: 'iv_c29', nome: 'Aprovado por', tipo: 'texto', obrigatorio: false },
        { id: 'iv_c30', nome: 'Ajuste lançado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'iv_e6', nome: 'Encerrado',
      camposVisiveisNoCard: ['iv_c1', 'iv_c19', 'iv_c31'],
      permiteCriarCards: false,
      campos: [
        { id: 'iv_c31', nome: 'Data do Encerramento', tipo: 'data', obrigatorio: false },
        { id: 'iv_c32', nome: 'Plano de Ação', tipo: 'texto_longo', obrigatorio: false },
        { id: 'iv_c33', nome: 'Próximo Inventário da Área', tipo: 'data', obrigatorio: false },
        { id: 'iv_c34', nome: 'Relatório Final', tipo: 'anexo', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Com divergência abre recontagem',
      etapaOrigemId: 'iv_e2',
      condicao: { campoId: 'iv_c13', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'iv_e3',
      etapaDestinoSenaoId: 'iv_e4',
    },
    {
      tipo: 'mover',
      nome: 'Recontagem concluída vai para análise',
      etapaOrigemId: 'iv_e3',
      condicao: { campoId: 'iv_c17', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'iv_e4',
    },
    {
      tipo: 'mover',
      nome: 'Ajuste aprovado vai para lançamento',
      etapaOrigemId: 'iv_e4',
      condicao: { campoId: 'iv_c25', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'iv_e5',
    },
    {
      tipo: 'mover',
      nome: 'Ajuste lançado encerra o inventário',
      etapaOrigemId: 'iv_e5',
      condicao: { campoId: 'iv_c30', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'iv_e6',
    },
  ],
  cards: [
    { id: 'iv_card1', titulo: 'Rotativo curva A - Mercearia', etapaId: 'iv_e4', etiquetas: ['Curva A'], valores: { iv_c1: 'Rotativo curva A - Mercearia', iv_c2: 'Rotativo (por curva)', iv_c3: '07 CD SC', iv_c6: 180, iv_c11: 22, iv_c19: 87.8, iv_c21: 34000 } },
    { id: 'iv_card2', titulo: 'Inventário geral - Loja Matriz', etapaId: 'iv_e2', etiquetas: ['Inventário Geral'], valores: { iv_c1: 'Inventário geral - Loja Matriz', iv_c2: 'Geral / balanço', iv_c3: '01 MATRIZ', iv_c6: 12400, iv_c8: 'Sim', iv_c9: 45, iv_c10: 'Sim' } },
    { id: 'iv_card3', titulo: 'Cíclico ruas 10 a 14 - CD', etapaId: 'iv_e3', valores: { iv_c1: 'Cíclico ruas 10 a 14 - CD', iv_c2: 'Cíclico (por endereço)', iv_c3: '07 CD SC', iv_c6: 320, iv_c11: 41, iv_c14: 41 } },
    { id: 'iv_card4', titulo: 'Pontual bebidas - suspeita de desvio', etapaId: 'iv_e5', etiquetas: ['Divergência Alta'], valores: { iv_c1: 'Pontual bebidas - suspeita de desvio', iv_c2: 'Pontual (por suspeita)', iv_c3: '11 VILA VELHA', iv_c19: 71.2, iv_c23: 12800, iv_c26: 'AJ-2026-0442' } },
    { id: 'iv_card5', titulo: 'Rotativo hortifruti - semanal', etapaId: 'iv_e6', valores: { iv_c1: 'Rotativo hortifruti - semanal', iv_c2: 'Rotativo (por curva)', iv_c3: '01 MATRIZ', iv_c19: 96.4, iv_c32: 'Reforçar baixa diária de descarte na seção.' } },
    { id: 'iv_card6', titulo: 'Fiscal - fechamento do trimestre', etapaId: 'iv_e1', valores: { iv_c1: 'Fiscal - fechamento do trimestre', iv_c2: 'Fiscal / contábil', iv_c3: '07 CD SC', iv_c6: 8600 } },
  ],
  tour: [
    '🔢 Acuracidade medida de verdade: contagem às cegas, recontagem e causa identificada.',
    '1️⃣ "Programação": tipo do inventário, área e se a operação para.',
    '2️⃣ "1ª Contagem": progresso e número de SKUs divergentes.',
    '3️⃣ "Recontagem": só o que divergiu é recontado — economiza equipe.',
    '4️⃣ "Análise": acuracidade em %, valor da diferença e as causas marcadas em checklist.',
    '5️⃣ "Ajuste Lançado": documento e aprovador registrados.',
    '📊 "Encerrado": plano de ação e data do próximo inventário da mesma área.',
    '💡 Acuracidade abaixo de 95% explica quase toda ruptura de gôndola.',
  ],
  etapasDicas: [
    { quemFaz: 'Controle de estoque', objetivo: 'Priorizar curva A — 20% dos itens são 80% do valor.' },
    { quemFaz: 'Equipe de contagem', objetivo: 'Contar às cegas; contagem com saldo à vista vira confirmação, não conferência.', automacoes: ['Já configurado: com divergência abre recontagem'] },
    { quemFaz: 'Segunda equipe', objetivo: 'Recontar apenas divergências, com pessoas diferentes da primeira contagem.' },
    { quemFaz: 'Controladoria / Estoque', objetivo: 'Achar a causa — ajustar sem causa é repetir o erro no mês seguinte.' },
    { quemFaz: 'Gerência / Controladoria', objetivo: 'Aprovar o ajuste com rastro de quem autorizou.' },
    { quemFaz: 'Controle de estoque', objetivo: 'Fechar com plano de ação e agendar o próximo.', conexoes: ['Ligue a "Perdas e Quebras" — baixa não lançada é causa clássica de divergência'] },
  ],
};

const devolucoesReversa: DefaultTemplate = {
  id: 'tpl_devolucoes_reversa',
  name: 'Devoluções e Logística Reversa',
  descricao: 'Tudo que volta em um só fluxo: recusa do cliente, devolução de loja para o CD e devolução ao fornecedor. Com coleta, conferência do retorno, destino do produto e emissão da NF de devolução ou crédito.',
  categoria: 'logistica',
  icone: '↩️',
  etiquetas: [
    { nome: 'Avaria', cor: COR.vermelho },
    { nome: 'Vencido', cor: COR.amarelo },
    { nome: 'Erro Nosso', cor: COR.roxo },
  ],
  etapas: [
    {
      id: 'dv_e1', nome: 'Solicitação de Devolução',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['dv_c1', 'dv_c2', 'dv_c6'],
      permiteMoverPara: ['dv_e2'],
      campos: [
        { id: 'dv_c1', nome: 'Produto / Carga', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'dv_c2', nome: 'Fluxo da Devolução', tipo: 'lista', obrigatorio: true, opcoes: ['Cliente devolve para nós', 'Loja devolve para o CD', 'Nós devolvemos ao fornecedor', 'Recusa na entrega', 'Troca por garantia'], coresOpcoes: { 'Nós devolvemos ao fornecedor': COR.verde, 'Recusa na entrega': COR.vermelho } },
        { id: 'dv_c3', nome: 'Cliente / Fornecedor / Loja', tipo: 'texto', obrigatorio: true },
        { id: 'dv_c4', nome: 'NF de Origem', tipo: 'texto', obrigatorio: false },
        { id: 'dv_c5', nome: 'Quantidade', tipo: 'numero', obrigatorio: false },
        { id: 'dv_c6', nome: 'Valor Envolvido', tipo: 'valor', obrigatorio: true },
        { id: 'dv_c7', nome: 'Motivo', tipo: 'lista', obrigatorio: true, opcoes: ['Produto avariado', 'Produto vencido', 'Validade curta', 'Erro de separação', 'Item não pedido', 'Divergência de preço', 'Excesso de estoque', 'Desistência do cliente', 'Defeito de fabricação'], coresOpcoes: { 'Produto avariado': COR.vermelho, 'Erro de separação': COR.roxo, 'Produto vencido': COR.amarelo } },
        { id: 'dv_c8', nome: 'Fotos', tipo: 'anexo', obrigatorio: false },
        { id: 'dv_c9', nome: 'Descrição', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'dv_e2', nome: 'Análise',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['dv_c1', 'dv_c10', 'dv_c11'],
      permiteMoverPara: ['dv_e3', 'dv_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'dv_c10', nome: 'Devolução procedente?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'dv_c11', nome: 'Responsabilidade', tipo: 'lista', obrigatorio: false, opcoes: ['Nossa (separação/expedição)', 'Transportadora', 'Fornecedor', 'Cliente', 'Sem responsável definido'], coresOpcoes: { 'Nossa (separação/expedição)': COR.roxo, 'Transportadora': COR.amarelo } },
        { id: 'dv_c12', nome: 'Está no prazo de troca?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'dv_c13', nome: 'Parecer', tipo: 'texto_longo', obrigatorio: false },
        { id: 'dv_c14', nome: 'Autorizado por', tipo: 'texto', obrigatorio: false },
      ],
    },
    {
      id: 'dv_e3', nome: 'Coleta / Retorno',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['dv_c1', 'dv_c16', 'dv_c15'],
      permiteMoverPara: ['dv_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'dv_c15', nome: 'Prazo da Coleta', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'dv_c16', nome: 'Forma do Retorno', tipo: 'lista', obrigatorio: false, opcoes: ['Coleta pela nossa frota', 'Transportadora', 'Cliente traz ao CD', 'Junto com a próxima entrega', 'Correios / transportadora fracionada'] },
        { id: 'dv_c17', nome: 'NF de Devolução do Cliente', tipo: 'texto', obrigatorio: false },
        { id: 'dv_c18', nome: 'Retorno realizado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'dv_c19', nome: 'Documentos do Retorno', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'dv_e4', nome: 'Conferência do Retorno',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['dv_c1', 'dv_c20', 'dv_c22'],
      permiteMoverPara: ['dv_e5'],
      permiteCriarCards: false,
      campos: [
        { id: 'dv_c20', nome: 'Quantidade Retornada', tipo: 'numero', obrigatorio: false },
        { id: 'dv_c21', nome: 'Confere com o solicitado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'dv_c22', nome: 'Estado do Produto', tipo: 'lista', obrigatorio: true, opcoes: ['Íntegro / revendável', 'Embalagem danificada', 'Produto danificado', 'Vencido', 'Contaminado / impróprio'], coresOpcoes: { 'Íntegro / revendável': COR.verde, 'Contaminado / impróprio': COR.vermelho } },
        { id: 'dv_c23', nome: 'Conferente', tipo: 'texto', obrigatorio: false },
        { id: 'dv_c24', nome: 'Divergência no Retorno', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'dv_e5', nome: 'Destino do Produto',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['dv_c1', 'dv_c25', 'dv_c27'],
      permiteMoverPara: ['dv_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'dv_c25', nome: 'Destino', tipo: 'lista', obrigatorio: true, opcoes: ['Volta ao estoque para revenda', 'Devolução ao fornecedor', 'Venda como avaria (outlet)', 'Descarte', 'Doação', 'Conserto / recondicionamento'], coresOpcoes: { 'Volta ao estoque para revenda': COR.verde, 'Descarte': COR.vermelho } },
        { id: 'dv_c26', nome: 'Endereço no Estoque', tipo: 'texto', obrigatorio: false },
        { id: 'dv_c27', nome: 'Valor Recuperado', tipo: 'valor', obrigatorio: false },
        { id: 'dv_c28', nome: 'Perda Efetiva', tipo: 'valor', obrigatorio: false },
        { id: 'dv_c29', nome: 'Destino executado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'dv_e6', nome: 'Crédito / Encerrado',
      camposVisiveisNoCard: ['dv_c1', 'dv_c30', 'dv_c32'],
      permiteCriarCards: false,
      campos: [
        { id: 'dv_c30', nome: 'Forma de Ressarcimento', tipo: 'lista', obrigatorio: false, opcoes: ['NF de devolução emitida', 'Crédito em conta corrente do cliente', 'Troca por outro produto', 'Abatimento no próximo pedido', 'Estorno financeiro', 'Sem ressarcimento'] },
        { id: 'dv_c31', nome: 'Nº da NF de Devolução', tipo: 'texto', obrigatorio: false },
        { id: 'dv_c32', nome: 'Valor Creditado', tipo: 'valor', obrigatorio: false },
        { id: 'dv_c33', nome: 'Data do Encerramento', tipo: 'data', obrigatorio: false },
        { id: 'dv_c34', nome: 'Gerou ação preventiva?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'dv_c35', nome: 'Ação Preventiva', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'dv_e7', nome: 'Recusada',
      camposVisiveisNoCard: ['dv_c1', 'dv_c36'],
      permiteMoverPara: ['dv_e2'],
      permiteCriarCards: false,
      campos: [
        { id: 'dv_c36', nome: 'Motivo da Recusa', tipo: 'lista', obrigatorio: true, opcoes: ['Fora do prazo de troca', 'Mau uso do cliente', 'Produto sem defeito', 'Sem nota fiscal de origem', 'Não é responsabilidade nossa'], coresOpcoes: { 'Mau uso do cliente': COR.cinza, 'Fora do prazo de troca': COR.cinza } },
        { id: 'dv_c37', nome: 'Justificativa ao Solicitante', tipo: 'texto_longo', obrigatorio: false },
        { id: 'dv_c38', nome: 'Solicitante comunicado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Procedente autoriza a coleta',
      etapaOrigemId: 'dv_e2',
      condicao: { campoId: 'dv_c10', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'dv_e3',
      etapaDestinoSenaoId: 'dv_e7',
    },
    {
      tipo: 'mover',
      nome: 'Retorno realizado vai para conferência',
      etapaOrigemId: 'dv_e3',
      condicao: { campoId: 'dv_c18', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'dv_e4',
    },
    {
      tipo: 'mover',
      nome: 'Destino executado gera o crédito',
      etapaOrigemId: 'dv_e5',
      condicao: { campoId: 'dv_c29', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'dv_e6',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Ação preventiva quando houver',
      etapaOrigemId: 'dv_e6',
      condicao: { campoId: 'dv_c34', operador: 'igual', valor: 'Sim' },
      camposAfetadosIds: ['dv_c35'],
    },
  ],
  cards: [
    { id: 'dv_card1', titulo: 'Rede Bom Preço - recusa parcial de 18 cx', etapaId: 'dv_e4', etiquetas: ['Avaria'], valores: { dv_c1: 'Rede Bom Preço - recusa parcial de 18 cx', dv_c2: 'Recusa na entrega', dv_c3: 'Rede Bom Preço', dv_c5: 18, dv_c6: 8600, dv_c7: 'Produto avariado', dv_c10: 'Sim', dv_c11: 'Transportadora', dv_c22: 'Embalagem danificada' } },
    { id: 'dv_card2', titulo: 'Iogurtes vencidos - loja Matriz para CD', etapaId: 'dv_e5', etiquetas: ['Vencido'], valores: { dv_c1: 'Iogurtes vencidos - loja Matriz para CD', dv_c2: 'Loja devolve para o CD', dv_c3: '01 MATRIZ', dv_c5: 34, dv_c6: 302, dv_c7: 'Produto vencido', dv_c25: 'Devolução ao fornecedor', dv_c27: 302 } },
    { id: 'dv_card3', titulo: 'Devolução ao fornecedor - lote com validade curta', etapaId: 'dv_e3', valores: { dv_c1: 'Devolução ao fornecedor - lote com validade curta', dv_c2: 'Nós devolvemos ao fornecedor', dv_c3: 'Laticínios Vale', dv_c5: 60, dv_c6: 5400, dv_c7: 'Validade curta', dv_c16: 'Coleta pela nossa frota' } },
    { id: 'dv_card4', titulo: 'Mercado Central - erro de separação', etapaId: 'dv_e6', etiquetas: ['Erro Nosso'], valores: { dv_c1: 'Mercado Central - erro de separação', dv_c2: 'Cliente devolve para nós', dv_c3: 'Mercado Central', dv_c6: 2400, dv_c7: 'Erro de separação', dv_c30: 'NF de devolução emitida', dv_c32: 2400, dv_c34: 'Sim', dv_c35: 'Dupla conferência obrigatória para pedidos acima de 40 itens.' } },
    { id: 'dv_card5', titulo: 'Atacado Sul - excesso de estoque', etapaId: 'dv_e2', valores: { dv_c1: 'Atacado Sul - excesso de estoque', dv_c2: 'Cliente devolve para nós', dv_c3: 'Atacado Sul', dv_c6: 18000, dv_c7: 'Excesso de estoque' } },
    { id: 'dv_card6', titulo: 'Padaria Estrela - liquidificador com defeito', etapaId: 'dv_e7', valores: { dv_c1: 'Padaria Estrela - liquidificador com defeito', dv_c2: 'Troca por garantia', dv_c3: 'Padaria Estrela', dv_c6: 480, dv_c7: 'Defeito de fabricação', dv_c36: 'Fora do prazo de troca', dv_c38: 'Sim' } },
  ],
  tour: [
    '↩️ Tudo que volta em um quadro só — cliente, loja e fornecedor.',
    '1️⃣ "Solicitação": o fluxo da devolução define quem paga a conta no fim.',
    '2️⃣ "Análise": procedente ou não, e de quem é a responsabilidade.',
    '3️⃣ "Coleta / Retorno": forma do retorno e prazo controlados.',
    '4️⃣ "Conferência": estado real do produto que voltou.',
    '5️⃣ "Destino": volta ao estoque, vira outlet, volta ao fornecedor ou é descarte.',
    '💰 "Crédito": NF de devolução ou abatimento, com valor recuperado x perda efetiva.',
    '📊 Filtre por "Erro Nosso" para ver quanto a operação custa em devolução evitável.',
  ],
  etapasDicas: [
    { quemFaz: 'Atendimento / Vendedor / Loja', objetivo: 'Registrar com foto e nota fiscal de origem.' },
    { quemFaz: 'Supervisor / Qualidade', objetivo: 'Definir responsabilidade — é o que permite cobrar transportadora e fornecedor.', automacoes: ['Já configurado: improcedente vai para recusada com justificativa'] },
    { quemFaz: 'Logística', objetivo: 'Aproveitar a rota de entrega para a coleta e economizar frete.' },
    { quemFaz: 'Recebimento', objetivo: 'Conferir o que voltou de fato, não o que foi prometido.' },
    { quemFaz: 'Estoque / Comercial', objetivo: 'Recuperar valor sempre que possível antes de descartar.', conexoes: ['Ligue a "Perdas e Quebras" quando o destino for descarte'] },
    { quemFaz: 'Financeiro / Fiscal', objetivo: 'Emitir a NF de devolução e fechar o crédito.' },
    { quemFaz: 'Atendimento', objetivo: 'Comunicar a recusa com justificativa clara.' },
  ],
};

const reposicaoCompras: DefaultTemplate = {
  id: 'tpl_reposicao_compras',
  name: 'Reposição e Planejamento de Compras',
  descricao: 'Sugestão de compra a partir de cobertura e giro, análise do comprador com sazonalidade e lead time, negociação, alçada de aprovação e follow-up do pedido até a chegada. Evita ruptura e excesso ao mesmo tempo.',
  categoria: 'logistica',
  icone: '📈',
  etiquetas: [
    { nome: 'Risco de Ruptura', cor: COR.vermelho },
    { nome: 'Excesso de Estoque', cor: COR.amarelo },
    { nome: 'Compra Sazonal', cor: COR.roxo },
    { nome: 'Oportunidade', cor: COR.verde },
  ],
  etapas: [
    {
      id: 'rp_e1', nome: 'Sugestão de Compra',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['rp_c1', 'rp_c4', 'rp_c6'],
      permiteMoverPara: ['rp_e2'],
      campos: [
        { id: 'rp_c1', nome: 'Produto / Família', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'rp_c2', nome: 'Fornecedor', tipo: 'texto', obrigatorio: false },
        { id: 'rp_c3', nome: 'Curva', tipo: 'lista', obrigatorio: false, opcoes: ['A', 'B', 'C'], coresOpcoes: { 'A': COR.roxo, 'B': COR.azul, 'C': COR.cinza } },
        { id: 'rp_c4', nome: 'Cobertura Atual (dias)', tipo: 'numero', obrigatorio: false },
        { id: 'rp_c5', nome: 'Giro Médio Mensal', tipo: 'numero', obrigatorio: false },
        { id: 'rp_c6', nome: 'Quantidade Sugerida', tipo: 'numero', obrigatorio: true },
        { id: 'rp_c7', nome: 'Estoque Atual', tipo: 'numero', obrigatorio: false },
        { id: 'rp_c8', nome: 'Ponto de Pedido', tipo: 'numero', obrigatorio: false },
        { id: 'rp_c9', nome: 'Origem da Sugestão', tipo: 'lista', obrigatorio: false, opcoes: ['Sistema (ponto de pedido)', 'Ruptura reportada pela loja', 'Ação promocional prevista', 'Sazonalidade', 'Oportunidade de preço', 'Novo item'], coresOpcoes: { 'Ruptura reportada pela loja': COR.vermelho, 'Oportunidade de preço': COR.verde } },
      ],
    },
    {
      id: 'rp_e2', nome: 'Análise do Comprador',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['rp_c1', 'rp_c12', 'rp_c13'],
      permiteMoverPara: ['rp_e3', 'rp_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'rp_c10', nome: 'Lead Time do Fornecedor (dias)', tipo: 'numero', obrigatorio: false },
        { id: 'rp_c11', nome: 'Fatores Considerados', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['Sazonalidade', 'Promoção programada', 'Lote mínimo do fornecedor', 'Validade do produto', 'Capacidade de armazenagem', 'Preço em alta/baixa', 'Histórico de ruptura'] },
        { id: 'rp_c12', nome: 'Quantidade Ajustada', tipo: 'numero', obrigatorio: false },
        { id: 'rp_c13', nome: 'Comprar?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'rp_c14', nome: 'Justificativa', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'rp_e3', nome: 'Negociação',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['rp_c1', 'rp_c15', 'rp_c19'],
      permiteMoverPara: ['rp_e4', 'rp_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'rp_c15', nome: 'Preço Negociado', tipo: 'valor', obrigatorio: false },
        { id: 'rp_c16', nome: 'Preço Anterior', tipo: 'valor', obrigatorio: false },
        { id: 'rp_c17', nome: 'Prazo de Pagamento', tipo: 'texto', obrigatorio: false },
        { id: 'rp_c18', nome: 'Ganhos Negociados', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['Bonificação em produto', 'Desconto por volume', 'Frete CIF', 'Prazo estendido', 'Verba de encarte', 'Troca de vencidos'] },
        { id: 'rp_c19', nome: 'Valor Total do Pedido', tipo: 'valor', obrigatorio: true },
        { id: 'rp_c20', nome: 'Negociação concluída?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'rp_e4', nome: 'Aprovação',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['rp_c1', 'rp_c19', 'rp_c21'],
      permiteMoverPara: ['rp_e5', 'rp_e7'],
      permiteCriarCards: false,
      limparCamposAoRetornar: ['rp_c21', 'rp_c22'],
      campos: [
        { id: 'rp_c21', nome: 'Aprovado?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'rp_c22', nome: 'Observação da Aprovação', tipo: 'texto_longo', obrigatorio: false },
        { id: 'rp_c23', nome: 'Aprovador', tipo: 'texto', obrigatorio: false },
        { id: 'rp_c24', nome: 'Impacto no Fluxo de Caixa', tipo: 'valor', obrigatorio: false },
      ],
    },
    {
      id: 'rp_e5', nome: 'Pedido Colocado',
      camposVisiveisNoCard: ['rp_c1', 'rp_c25', 'rp_c26'],
      permiteMoverPara: ['rp_e6', 'rp_e7'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'rp_c25', nome: 'Nº do Pedido', tipo: 'texto', obrigatorio: false },
        { id: 'rp_c26', nome: 'Previsão de Entrega', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'rp_c27', nome: 'Pedido Confirmado pelo Fornecedor', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'rp_c28', nome: 'Follow-up', tipo: 'texto_longo', obrigatorio: false },
        { id: 'rp_c29', nome: 'Mercadoria recebida?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'rp_e6', nome: 'Recebido',
      camposVisiveisNoCard: ['rp_c1', 'rp_c30', 'rp_c31'],
      permiteCriarCards: false,
      campos: [
        { id: 'rp_c30', nome: 'Data do Recebimento', tipo: 'data', obrigatorio: false },
        { id: 'rp_c31', nome: 'Quantidade Recebida', tipo: 'numero', obrigatorio: false },
        { id: 'rp_c32', nome: 'Entregue no prazo?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'rp_c33', nome: 'Atendimento do Fornecedor (%)', tipo: 'numero', obrigatorio: false },
        { id: 'rp_c34', nome: 'Observação', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'rp_e7', nome: 'Não Comprado',
      camposVisiveisNoCard: ['rp_c1', 'rp_c35'],
      permiteMoverPara: ['rp_e1'],
      permiteCriarCards: false,
      campos: [
        { id: 'rp_c35', nome: 'Motivo', tipo: 'lista', obrigatorio: true, opcoes: ['Estoque suficiente', 'Preço acima do aceitável', 'Fornecedor sem disponibilidade', 'Restrição de caixa', 'Lote mínimo inviável', 'Item em descontinuação'], coresOpcoes: { 'Restrição de caixa': COR.vermelho, 'Fornecedor sem disponibilidade': COR.amarelo } },
        { id: 'rp_c36', nome: 'Detalhamento', tipo: 'texto_longo', obrigatorio: false },
        { id: 'rp_c37', nome: 'Reavaliar em', tipo: 'data', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Decisão de comprar abre a negociação',
      etapaOrigemId: 'rp_e2',
      condicao: { campoId: 'rp_c13', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'rp_e3',
      etapaDestinoSenaoId: 'rp_e7',
    },
    {
      tipo: 'mover',
      nome: 'Negociação fechada vai para aprovação',
      etapaOrigemId: 'rp_e3',
      condicao: { campoId: 'rp_c20', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'rp_e4',
    },
    {
      tipo: 'mover',
      nome: 'Aprovado coloca o pedido',
      etapaOrigemId: 'rp_e4',
      condicao: { campoId: 'rp_c21', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'rp_e5',
      etapaDestinoSenaoId: 'rp_e7',
    },
    {
      tipo: 'mover',
      nome: 'Mercadoria recebida encerra o ciclo',
      etapaOrigemId: 'rp_e5',
      condicao: { campoId: 'rp_c29', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'rp_e6',
    },
  ],
  cards: [
    { id: 'rp_card1', titulo: 'Óleo de soja 900ml - reposição urgente', etapaId: 'rp_e3', etiquetas: ['Risco de Ruptura'], valores: { rp_c1: 'Óleo de soja 900ml - reposição urgente', rp_c2: 'Alimentos Vale', rp_c3: 'A', rp_c4: 3, rp_c6: 1200, rp_c9: 'Ruptura reportada pela loja', rp_c15: 6.4, rp_c19: 76800 } },
    { id: 'rp_card2', titulo: 'Panetone 400g - compra de natal', etapaId: 'rp_e4', etiquetas: ['Compra Sazonal'], valores: { rp_c1: 'Panetone 400g - compra de natal', rp_c2: 'Alimentos Vale', rp_c3: 'B', rp_c6: 4000, rp_c9: 'Sazonalidade', rp_c19: 46000, rp_c24: 46000 } },
    { id: 'rp_card3', titulo: 'Café 500g - oportunidade de preço', etapaId: 'rp_e2', etiquetas: ['Oportunidade'], valores: { rp_c1: 'Café 500g - oportunidade de preço', rp_c2: 'Torrefação Sul', rp_c3: 'A', rp_c4: 22, rp_c5: 3200, rp_c6: 6000, rp_c9: 'Oportunidade de preço', rp_c10: 7 } },
    { id: 'rp_card4', titulo: 'Sabão em pó 1,6kg - reposição', etapaId: 'rp_e5', valores: { rp_c1: 'Sabão em pó 1,6kg - reposição', rp_c2: 'Química Brasil', rp_c3: 'A', rp_c6: 900, rp_c19: 19800, rp_c25: 'PC-88412', rp_c27: 'Sim' } },
    { id: 'rp_card5', titulo: 'Bazar - utilidades domésticas', etapaId: 'rp_e6', valores: { rp_c1: 'Bazar - utilidades domésticas', rp_c2: 'Utilidades Brasil', rp_c3: 'C', rp_c6: 300, rp_c31: 300, rp_c32: 'Sim', rp_c33: 100 } },
    { id: 'rp_card6', titulo: 'Refrigerante 2L - reposição semanal', etapaId: 'rp_e1', etiquetas: ['Excesso de Estoque'], valores: { rp_c1: 'Refrigerante 2L - reposição semanal', rp_c2: 'BebidasMax', rp_c3: 'A', rp_c4: 46, rp_c5: 2800, rp_c6: 0, rp_c9: 'Sistema (ponto de pedido)' } },
    { id: 'rp_card7', titulo: 'Azeite 500ml - reposição', etapaId: 'rp_e7', valores: { rp_c1: 'Azeite 500ml - reposição', rp_c2: 'Importadora Ibéria', rp_c3: 'C', rp_c6: 200, rp_c35: 'Preço acima do aceitável', rp_c36: 'Fornecedor subiu 22% sem aviso. Buscar alternativa nacional.' } },
  ],
  tour: [
    '📈 Compra pensada: cobertura, giro e lead time decidindo o quanto pedir.',
    '1️⃣ "Sugestão": cobertura em dias e ponto de pedido lado a lado com o estoque atual.',
    '2️⃣ "Análise do Comprador": sazonalidade, promoção prevista e lote mínimo ajustam a quantidade.',
    '3️⃣ "Negociação": preço, prazo, bonificação e verba registrados.',
    '4️⃣ "Aprovação": impacto no fluxo de caixa visível para quem aprova.',
    '5️⃣ "Pedido Colocado": previsão de entrega vira vencimento — follow-up sem planilha paralela.',
    '📊 "Recebido": percentual de atendimento do fornecedor, o indicador que qualifica a base.',
  ],
  etapasDicas: [
    { quemFaz: 'Sistema / Analista de reposição', objetivo: 'Trazer a sugestão com cobertura real, não com achismo.', conexoes: ['Ligue a "Ruptura e Reposição de Gôndola" — ruptura vira sugestão de compra'] },
    { quemFaz: 'Comprador', objetivo: 'Ajustar pelo que o sistema não sabe: promoção, sazonal e espaço no CD.', automacoes: ['Já configurado: decisão de não comprar arquiva com motivo'] },
    { quemFaz: 'Comprador', objetivo: 'Negociar além do preço — bonificação e prazo valem margem.' },
    { quemFaz: 'Gerente Comercial / Financeiro', objetivo: 'Aprovar olhando o caixa, não só o custo unitário.' },
    { quemFaz: 'Comprador / Assistente', objetivo: 'Fazer follow-up antes do prazo estourar.', conexoes: ['Ligue a "Recebimento e Conferência" para o agendamento da doca'] },
    { quemFaz: 'Recebimento / Comprador', objetivo: 'Medir o atendimento do fornecedor a cada entrega.' },
    { quemFaz: 'Comprador', objetivo: 'Registrar o motivo e a data para reavaliar.' },
  ],
};

export const TEMPLATES_SUPPLY_CHAIN: DefaultTemplate[] = [
  pedidoExpedicao,
  recebimentoDocas,
  inventarioAcuracidade,
  devolucoesReversa,
  reposicaoCompras,
];
